import {
  addToast,
  Button,
  CheckInput,
  DateInput,
  Modal,
  Select,
  SelectOptionType,
  TextInput,
  Icon,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';
import {
  Period,
  PeriodAcademicCalendarEvent,
} from '../../../../api/requests/periods';
import { CardTitle } from '../../../../components/text';
import { useDateRange } from '../../../../hooks/useDateRange';
import { useValidations } from '../../../../hooks/useValidations';
import { AcademicCalendar, EventData } from '../PeriodsForm';
import styles from './EventForm.module.scss';
import { YesNoToggleInput } from '../../../../components/inputs/YesNoToogle';
import Tooltip from '../../../../components/tooltip/Tooltip';

export interface EventValues {
  event:
    | (SelectOptionType & {
        interval: string;
        hasRequiredCredits: boolean;
        hasProcessConfig: boolean;
      })
    | null;
  eventStartDate: string;
  eventEndDate: string;
  creditMin: number;
  creditMax: number;
  previousTuitionPeriodId?: SelectOptionType | null;
  requirePreviousTuition?: boolean | null;
  allowOnlineTuition?: boolean | null;
  allowSectionScheduleConflicts?: boolean | null;
}

function getDateInputsVisibility(eventInterval?: string) {
  if (!eventInterval) {
    return {
      showStartDate: false,
      showEndDate: false,
    };
  } else if (eventInterval === 'T') {
    return {
      showStartDate: false,
      showEndDate: true,
    };
  } else if (eventInterval === 'I') {
    return {
      showStartDate: true,
      showEndDate: false,
    };
  } else {
    return {
      showStartDate: true,
      showEndDate: true,
    };
  }
}

function getCreditsInputsVisibility(hasRequiredCredits?: boolean) {
  if (!hasRequiredCredits) {
    return {
      showCreditMin: false,
      showCreditMax: false,
    };
  }
  return {
    showCreditMin: true,
    showCreditMax: true,
  };
}

interface EventFormProps {
  periodName?: string;
  eventsList: PeriodAcademicCalendarEvent[];
  periodsList: Period[];
  changeEventsTable: (value: EventData, isEdit: boolean) => void;
  show: boolean;
  onClose: Function;
  selectedEventsIds: number[];
  recordToEdit: AcademicCalendar | null;
  periodCreditMin: number;
  periodCreditMax: number;
}

export const EventForm = ({
  periodName,
  eventsList,
  periodsList,
  changeEventsTable,
  show,
  onClose,
  selectedEventsIds,
  recordToEdit,
  periodCreditMin,
  periodCreditMax,
}: EventFormProps) => {
  const { msgValidations, rulesPatternNumeric } = useValidations();
  const { t } = useTranslation();
  const prefix = 'maintainers.periodsForm';

  const methods = useForm<EventValues>({
    mode: 'onSubmit',
    defaultValues: {
      event: null,
      eventStartDate: '',
      eventEndDate: '',
      creditMin: undefined,
      creditMax: undefined,
      previousTuitionPeriodId: null,
      requirePreviousTuition: null,
      allowOnlineTuition: null,
      allowSectionScheduleConflicts: null,
    },
  });

  const isEditionMode = !!recordToEdit;

  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
    reset,
    setValue,
    watch,
  } = methods;

  const [
    eventValue,
    eventStartDate,
    eventEndDate,
    eventCreditMin,
    eventCreditMax,
    requirePreviousTuition,
  ] = watch([
    'event',
    'eventStartDate',
    'eventEndDate',
    'creditMin',
    'creditMax',
    'requirePreviousTuition',
  ]);

  const eventsOptions = useMemo(() => {
    return eventsList.map(
      ({ id, name, interval, hasRequiredCredits, hasProcessConfig }) => ({
        label: name,
        value: id,
        isDisabled: selectedEventsIds.includes(id),
        interval,
        hasRequiredCredits,
        hasProcessConfig,
      }),
    );
  }, [eventsList, selectedEventsIds]);

  const periodsOptions = useMemo(() => {
    return periodsList?.map((p) => ({
      label: p.name,
      value: p.id,
    }));
  }, [periodsList]);

  // Default values
  useEffect(() => {
    if (show) {
      reset({
        event: recordToEdit
          ? {
              label: recordToEdit.eventCalendar.name,
              value: recordToEdit.eventCalendar.id,
              interval: recordToEdit.eventCalendar.interval,
              hasRequiredCredits: recordToEdit.eventCalendar.hasRequiredCredits,
              hasProcessConfig: recordToEdit.eventCalendar.hasProcessConfig,
            }
          : null,
        eventStartDate: recordToEdit?.startDate ?? '',
        eventEndDate: recordToEdit?.endDate ?? '',
        creditMin: periodCreditMin === 0 ? undefined : periodCreditMin,
        creditMax: periodCreditMax === 0 ? undefined : periodCreditMax,
        requirePreviousTuition: recordToEdit?.requirePreviousTuition,
        previousTuitionPeriodId: periodsOptions?.find(
          (p) => p.value === recordToEdit?.previousTuitionPeriodId,
        ),
        allowSectionScheduleConflicts:
          recordToEdit?.allowSectionScheduleConflicts,
        allowOnlineTuition: recordToEdit?.allowOnlineTuition,
      });
    }
  }, [
    show,
    recordToEdit,
    reset,
    periodCreditMin,
    periodCreditMax,
    periodsOptions,
  ]);

  const onSubmit = async (formValues: EventValues) => {
    if (!formValues.event) {
      return;
    }
    changeEventsTable(
      {
        eventId: +formValues.event.value,
        startDate: formValues.eventStartDate,
        endDate: formValues.eventEndDate,
        creditMin: formValues.creditMin,
        creditMax: formValues.creditMax,
        hasRequiredCredits: formValues.event.hasRequiredCredits,
        hasProcessConfig: formValues.event.hasProcessConfig,
        requirePreviousTuition: formValues.requirePreviousTuition,
        previousTuitionPeriodId: formValues.previousTuitionPeriodId?.value,
        allowSectionScheduleConflicts: formValues.allowSectionScheduleConflicts,
        allowOnlineTuition: formValues.allowOnlineTuition,
      },
      isEditionMode,
    );
    closeModal();
    addToast({
      icon: 'success',
      color: 'success',
      text: t(`${prefix}.createEventSuccess`),
    });
  };

  const closeModal = () => {
    reset();
    onClose();
  };

  const { showStartDate, showEndDate } = getDateInputsVisibility(
    eventValue?.interval,
  );

  const { showCreditMin, showCreditMax } = getCreditsInputsVisibility(
    eventValue?.hasRequiredCredits,
  );

  useEffect(() => {
    if (!showCreditMin) {
      setValue('creditMin', 0);
    }
  }, [showCreditMin, setValue]);

  useEffect(() => {
    if (!showCreditMax) {
      setValue('creditMax', 0);
    }
  }, [showCreditMax, setValue]);

  const showOnlyOneDate = showStartDate !== showEndDate;

  useEffect(() => {
    if (!showStartDate) {
      setValue('eventStartDate', '');
    }
  }, [showStartDate, setValue]);

  useEffect(() => {
    if (!showEndDate) {
      setValue('eventEndDate', '');
    }
  }, [showEndDate, setValue]);

  const setEndDate = useCallback(
    (e) => {
      if (!showOnlyOneDate) {
        setValue('eventEndDate', e);
      }
    },
    [setValue, showOnlyOneDate],
  );
  const { minDate, endDateDisabled } = useDateRange({
    startDate: eventStartDate,
    endDate: eventEndDate,
    setEndDate,
  });

  return (
    <Modal
      isOpen={show}
      toggle={() => {
        closeModal();
      }}
      size="lg"
    >
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <Row>
            <Col>
              <div className="text-center">
                <CardTitle
                  children={t(
                    `${prefix}.${isEditionMode ? 'editEvent' : 'newEvent'}`,
                  )}
                />
              </div>
              <Row className="mt-4 mt-md-5">
                <Col xs="12" md="5">
                  <div
                    className={`${styles.periodName} border-secondary d-flex flex-column text-secondary mb-4 mb-md-0`}
                  >
                    <span>{t(`${prefix}.periodName`)}</span>
                    <span>{periodName}</span>
                  </div>
                </Col>
                <Col xs="12" md="7">
                  <Select
                    disabled={!!recordToEdit}
                    name="event"
                    label={t(`${prefix}.event`)}
                    options={eventsOptions}
                    control={control}
                    rules={{ required: msgValidations.required }}
                  />
                </Col>
              </Row>
              <Row
                className={showStartDate || showEndDate ? 'mt-0 mt-md-2' : ''}
              >
                {showStartDate && (
                  <Col xs={12} md>
                    <DateInput
                      name="eventStartDate"
                      label={t(
                        showOnlyOneDate
                          ? 'common.forms.date'
                          : `common.forms.startDate`,
                      )}
                      control={control}
                      rules={{ required: msgValidations.required }}
                    />
                  </Col>
                )}
                {showEndDate && (
                  <Col xs={12} md>
                    <DateInput
                      name="eventEndDate"
                      label={t(
                        showOnlyOneDate
                          ? 'common.forms.date'
                          : 'common.forms.endDate',
                      )}
                      control={control}
                      rules={{ required: msgValidations.required }}
                      minDate={showOnlyOneDate ? undefined : minDate}
                      disabled={showOnlyOneDate ? false : endDateDisabled}
                    />
                  </Col>
                )}
              </Row>

              <Row
                className={showCreditMin || showCreditMax ? 'mt-0 mt-md-2' : ''}
              >
                {showCreditMin && (
                  <Col xs={12} md>
                    <TextInput
                      name="creditMin"
                      label={t('common.forms.creditMin')}
                      control={control}
                      rules={{
                        required: msgValidations.required,
                        min: {
                          value: 0,
                          message: t('common.validations.minNumber', {
                            num: 0,
                          }),
                        },
                        max: {
                          value: eventCreditMax,
                          message: t(`${prefix}.validations.maxCreditNumber`, {
                            num: eventCreditMax,
                          }),
                        },
                        pattern: rulesPatternNumeric,
                      }}
                    />
                  </Col>
                )}
                {showCreditMax && (
                  <Col xs={12} md>
                    <TextInput
                      name="creditMax"
                      label={t('common.forms.creditMax')}
                      control={control}
                      rules={{
                        required: msgValidations.required,
                        min: {
                          value: eventCreditMin,
                          message: t(`${prefix}.validations.minCreditNumber`, {
                            num: eventCreditMin,
                          }),
                        },
                        pattern: rulesPatternNumeric,
                      }}
                    />
                  </Col>
                )}
              </Row>

              {eventValue?.hasProcessConfig && (
                <Row
                  className={eventValue?.hasProcessConfig ? 'mt-0 mt-md-2' : ''}
                >
                  <Col xs={12}>
                    <div
                      className="d-flex align-items-left"
                      style={{ gap: '8px' }}
                    >
                      <CheckInput
                        name="requirePreviousTuition"
                        label={t(`${prefix}.requirePreviousTuition`)}
                        control={control}
                      />
                      <Tooltip
                        id={`info_tooltip_plus_requirePreviousTuition`}
                        placement="right"
                        text={t(`${prefix}.requirePreviousTuitionTooltip`)}
                      >
                        <Icon name="information" />
                      </Tooltip>
                    </div>
                  </Col>
                  {requirePreviousTuition && (
                    <Col xs={12}>
                      <Select
                        name="previousTuitionPeriodId"
                        label={t(`${prefix}.previousTuitionPeriodId`)}
                        options={periodsOptions}
                        control={control}
                        rules={{ required: msgValidations.required }}
                      />
                    </Col>
                  )}
                  <Col xs={12}>
                    <YesNoToggleInput
                      label={t(`${prefix}.allowOnlineTuition`)}
                      formKey={'allowOnlineTuition' as any}
                      rules={{
                        validate: {
                          require: (value: boolean) =>
                            value === true || value === false
                              ? undefined
                              : msgValidations.required,
                        },
                      }}
                    />
                  </Col>
                  <Col xs={12}>
                    <YesNoToggleInput
                      label={t(`${prefix}.allowSectionScheduleConflicts`)}
                      formKey={'allowSectionScheduleConflicts' as any}
                      rules={{
                        validate: {
                          require: (value: boolean) =>
                            value === true || value === false
                              ? undefined
                              : msgValidations.required,
                        },
                      }}
                    />
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
          <Row>
            <Col className="mt-4">
              <Row>
                <Col xs={{ size: 12, order: 2 }} md={{ size: 6, order: 1 }}>
                  <Button
                    type="button"
                    text={t('common.actions.cancel')}
                    outlined
                    onClick={closeModal}
                    fullwidth
                  />
                </Col>
                <Col xs={{ size: 12, order: 1 }} md={{ size: 6, order: 2 }}>
                  <Button
                    type="submit"
                    text={t(
                      isEditionMode
                        ? 'common.actions.saveChanges'
                        : `${prefix}.add`,
                    )}
                    loading={isSubmitting}
                    fullwidth
                    className="mb-3 mb-md-0"
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </FormProvider>
    </Modal>
  );
};
